import { unwrapList, unwrapObject, getAccessToken } from './apiUtils';
import { call, toNumber, TAX_RATE } from './SalesService';
import { getWarehouseId } from './TerminalService';
import { CartItem } from '../types/book.types';

/**
 * Reposicion de stock: pedidos a proveedores (ordenes de compra del backoffice)
 * de los libros que estan a 0 en el almacen de esta caja. La orden se crea, se
 * le anaden las lineas y se envia a aprobacion; aprobarla y recibirla se hace
 * en el backoffice (aprueba el supervisor del usuario que la crea).
 */
const ROUTES = {
    lowStock: '/stock_levels_list',
    suppliers: '/suppliers_list',
    warehouses: '/warehouses_list',
    createOrder: '/purchase_orders_create',
    addLine: '/purchase_order_lines_add',
    submit: '/purchase_orders_submit_for_approval',
    cancel: '/purchase_orders_cancel',
    listOrders: '/purchase_orders_list',
    lineClients: '/purchase_order_lines_clients'
};

export interface LowStockBook {
    id: string;
    title: string;
    author: string;
    isbn: string;
    price: number;
    category: string;
    /** Existencias en el almacen de esta caja. */
    stock: number;
    /** Unidades ya pedidas a proveedores en ordenes abiertas y aun no recibidas. */
    onOrder: number;
}

export interface LowStockPage {
    books: LowStockBook[];
    total: number;
}

export interface Supplier {
    id: string;
    name: string;
}

const mapLowStock = (raw: any): LowStockBook => ({
    id: String(raw.id),
    title: raw.title || '',
    author: raw.author || '',
    isbn: raw.isbn || '',
    price: toNumber(raw.price),
    category: raw.category || '',
    stock: toNumber(raw.stock),
    onOrder: toNumber(raw.on_order)
});

/** Libros de este almacen con existencias <= maxStock, incluidos los que nunca tuvieron movimientos. */
export const listLowStockBooks = async (options: {
    search?: string;
    maxStock?: number;
    page?: number;
    pageSize?: number;
} = {}): Promise<LowStockPage> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const res = await call(ROUTES.lowStock, {
        access_token: accessToken,
        warehouse_id: getWarehouseId(),
        max_stock: options.maxStock === undefined ? 0 : options.maxStock,
        search: options.search,
        page: options.page || 1,
        page_size: options.pageSize || 25
    });

    const rows = unwrapList(res);
    return {
        books: rows.map(mapLowStock),
        total: rows.length > 0 ? toNumber(rows[0].total_count) : 0
    };
};

const SUPPLIERS_TTL_MS = 60000;
let suppliersCache: { at: number; list: Supplier[] } | null = null;

/** Proveedores activos, para que el cajero elija a quien pedir. */
export const listActiveSuppliers = async (): Promise<Supplier[]> => {
    if (suppliersCache && Date.now() - suppliersCache.at < SUPPLIERS_TTL_MS) return suppliersCache.list;
    const list = await fetchActiveSuppliers();
    suppliersCache = { at: Date.now(), list };
    return list;
};

const fetchActiveSuppliers = async (): Promise<Supplier[]> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const res = await call(ROUTES.suppliers, {
        access_token: accessToken,
        status: 'active',
        page: 1,
        page_size: 200
    });
    return unwrapList(res).map(raw => ({ id: String(raw.id), name: raw.name || `Proveedor ${raw.id}` }));
};

/** La orden de compra exige la sociedad: es la del almacen de esta caja. */
const getWarehouseCompanyId = async (warehouseId: string): Promise<string> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const res = await call(ROUTES.warehouses, { access_token: accessToken, page: 1, page_size: 200 });
    const warehouse = unwrapList(res).find(w => String(w.id) === String(warehouseId));
    if (!warehouse) throw new Error('No se encontro el almacen de esta caja.');
    if (warehouse.company_id === undefined || warehouse.company_id === null) {
        throw new Error('El almacen de esta caja no tiene sociedad asignada.');
    }
    return String(warehouse.company_id);
};

export interface RestockLine {
    bookId: string;
    title: string;
    quantity: number;
}

/** Linea de orden que se pidio para un cliente (las que no llevan cliente no salen). */
interface ClientLine {
    lineId: string;
    orderId: string;
    clientName: string;
}

const fetchClientLines = async (filter: { orderId?: string; warehouseId?: string }): Promise<ClientLine[]> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const res = await call(ROUTES.lineClients, {
        access_token: accessToken,
        purchase_order_id: filter.orderId,
        warehouse_id: filter.warehouseId
    });
    return unwrapList(res).map(r => ({
        lineId: String(r.line_id),
        orderId: String(r.purchase_order_id),
        clientName: r.client_name || ''
    }));
};

export interface CreatedPurchaseOrder {
    id: string;
    number: string;
}

/**
 * La cabecera devuelve la fila creada; si por lo que sea no trae el id se busca
 * la ultima orden en borrador de ese proveedor y almacen.
 */
const resolveCreatedOrder = async (
    created: any,
    supplierId: string,
    warehouseId: string
): Promise<{ id: string; number: string }> => {
    if (created && created.id) {
        return { id: String(created.id), number: String(created.po_number || created.id) };
    }

    const accessToken = getAccessToken();
    const res = await call(ROUTES.listOrders, {
        access_token: accessToken,
        supplier_id: supplierId,
        warehouse_id: warehouseId,
        status: 'draft',
        page: 1,
        page_size: 1
    });
    const latest = unwrapList(res)[0];
    if (!latest || !latest.id) throw new Error('El backend no devolvio el id de la orden de compra.');
    return { id: String(latest.id), number: String(latest.po_number || latest.id) };
};

/**
 * Crea la orden de compra, anade una linea por libro y la envia a aprobacion.
 *
 * Los endpoints no son atomicos (una linea por llamada), asi que si algo falla
 * a mitad se cancela el borrador para no dejar ordenes a medias en el
 * backoffice. El precio de coste va a 0: el catalogo solo tiene el de venta y
 * compras lo completa al aprobar.
 */
export const createRestockOrder = async (
    supplierId: string,
    lines: RestockLine[],
    clientId?: string,
    notes = 'Reposicion de stock desde el TPV'
): Promise<CreatedPurchaseOrder> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');
    if (lines.length === 0) throw new Error('Selecciona al menos un libro.');

    const warehouseId = getWarehouseId();
    if (!warehouseId) throw new Error('Esta caja no tiene almacen asignado: no se puede pedir mercancia.');

    const companyId = await getWarehouseCompanyId(warehouseId);

    const createRes = await call(ROUTES.createOrder, {
        access_token: accessToken,
        company_id: companyId,
        supplier_id: supplierId,
        warehouse_id: warehouseId,
        notes
    });
    const order = await resolveCreatedOrder(unwrapObject(createRes), supplierId, warehouseId);

    try {
        for (const line of lines) {
            await call(ROUTES.addLine, {
                access_token: accessToken,
                purchase_order_id: order.id,
                book_id: line.bookId,
                description: line.title,
                quantity_ordered: line.quantity,
                unit_price: '0.00',
                tax_rate: TAX_RATE,
                client_id: clientId
            });
        }

        // purchase_order_lines_add ignora los parametros que no conoce: una
        // version antigua del endpoint crearia el pedido SIN cliente y nadie lo
        // notaria. Se comprueba que el cliente quedo guardado antes de enviarlo.
        if (clientId) {
            const saved = await fetchClientLines({ orderId: order.id });
            if (saved.length < lines.length) {
                throw new Error('El servidor no ha guardado el cliente del pedido (falta actualizar el endpoint de lineas).');
            }
        }

        await call(ROUTES.submit, { access_token: accessToken, id: order.id });
    } catch (err) {
        let cancelled = false;
        try {
            await call(ROUTES.cancel, { access_token: accessToken, id: order.id });
            cancelled = true;
        } catch { /* la orden queda en borrador: se avisa abajo */ }
        throw new Error(
            `${err?.message || 'Error desconocido'} La orden ${order.number} ${cancelled ? 'se ha cancelado' : 'ha quedado en borrador'}.`
        );
    }

    return { id: order.id, number: order.number };
};

/**
 * Pedidos de cliente que genera una venta: las lineas de PEDIDO (libro sin stock
 * con proveedor elegido) se agrupan por proveedor y se crea una orden de compra
 * por cada uno, para el cliente de la venta y enviada ya a aprobacion. Las notas
 * guardan el ticket de origen.
 *
 * Devuelve "OC-... (Proveedor)" de cada pedido creado. Si uno falla lanza, y los
 * anteriores quedan creados: la venta ya esta cobrada y no se deshace.
 */
export const placeCustomerOrders = async (
    items: CartItem[],
    clientId: string,
    ticketNumber: string
): Promise<string[]> => {
    const bySupplier: Record<string, CartItem[]> = {};
    items.forEach(item => {
        if (!item.selectedSource || !item.selectedSource.id) return;
        const supplierId = item.selectedSource.id;
        (bySupplier[supplierId] = bySupplier[supplierId] || []).push(item);
    });

    const placed: string[] = [];
    for (const supplierId of Object.keys(bySupplier)) {
        const lines = bySupplier[supplierId];
        const order = await createRestockOrder(
            supplierId,
            lines.map(l => ({ bookId: l.id, title: l.title, quantity: l.quantity })),
            clientId,
            `Pedido de cliente - Ticket ${ticketNumber}`
        );
        placed.push(`${order.number} (${lines[0].selectedSource.name})`);
    }
    return placed;
};

// ── Consulta de ordenes de compra (pantalla "Gestion de Pedidos") ──────────

export type PurchaseOrderStatus =
    'draft' | 'pending_approval' | 'approved' | 'partially_received' | 'received' | 'cancelled';

export interface PurchaseOrderSummary {
    id: string;
    number: string;
    supplierName: string;
    warehouseName: string;
    status: PurchaseOrderStatus;
    total: number;
    createdAt: Date;
    createdByName: string;
    approvedByName: string;
    expectedDelivery: string;
    /** Clientes para los que se pidio algun libro de la orden (vacio = reposicion de la tienda). */
    clients: string[];
}

export interface PurchaseOrderLine {
    id: string;
    title: string;
    quantityOrdered: number;
    quantityReceived: number;
    unitPrice: number;
    lineTotal: number;
    /** Cliente para el que se pidio esta linea; vacio si es para la tienda. */
    clientName: string;
}

export interface PurchaseOrderEvent {
    status: PurchaseOrderStatus;
    changedByName: string;
    changedAt: Date;
    note: string;
}

export interface PurchaseOrderDetail extends PurchaseOrderSummary {
    lines: PurchaseOrderLine[];
    history: PurchaseOrderEvent[];
    notes: string;
    paymentTerms: string;
}

const STATUSES: PurchaseOrderStatus[] =
    ['draft', 'pending_approval', 'approved', 'partially_received', 'received', 'cancelled'];

const mapOrder = (raw: any): PurchaseOrderSummary => ({
    id: String(raw.id),
    number: raw.po_number || String(raw.id),
    supplierName: raw.supplier_name || '',
    warehouseName: raw.warehouse_name || '',
    status: (STATUSES.indexOf(raw.status) === -1 ? 'draft' : raw.status) as PurchaseOrderStatus,
    total: toNumber(raw.total_amount),
    createdAt: new Date(raw.created_at || Date.now()),
    createdByName: raw.created_by_name || '',
    approvedByName: raw.approved_by_name || '',
    expectedDelivery: raw.expected_delivery_date || '',
    clients: []
});

export interface PurchaseOrderPage {
    orders: PurchaseOrderSummary[];
    total: number;
}

/** Ordenes de compra del almacen de esta caja, de la mas reciente a la mas antigua. */
export const listPurchaseOrders = async (options: {
    status?: PurchaseOrderStatus;
    page?: number;
    pageSize?: number;
} = {}): Promise<PurchaseOrderPage> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const res = await call(ROUTES.listOrders, {
        access_token: accessToken,
        warehouse_id: getWarehouseId(),
        status: options.status,
        page: options.page || 1,
        page_size: options.pageSize || 50
    });

    const body = unwrapObject(res) || {};
    const orders = unwrapList(res).map(mapOrder);

    // Los clientes viven en las lineas. Si ese endpoint falla o aun no esta
    // desplegado, el listado sale igual, solo que sin la columna de cliente.
    try {
        const byOrder: Record<string, string[]> = {};
        (await fetchClientLines({ warehouseId: getWarehouseId() || undefined })).forEach(l => {
            const names = byOrder[l.orderId] || (byOrder[l.orderId] = []);
            if (l.clientName && names.indexOf(l.clientName) === -1) names.push(l.clientName);
        });
        orders.forEach(o => { o.clients = byOrder[o.id] || []; });
    } catch { /* sin clientes */ }

    return { orders, total: toNumber(body.total, orders.length) };
};

/** Una orden con sus lineas e historial de estados. */
export const getPurchaseOrder = async (id: string): Promise<PurchaseOrderDetail> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const res = await call('/purchase_orders_get', { access_token: accessToken, id });
    const raw = unwrapObject(res);
    if (!raw || !raw.id) throw new Error('No se encontro la orden de compra.');

    const clientByLine: Record<string, string> = {};
    try {
        (await fetchClientLines({ orderId: id })).forEach(l => { clientByLine[l.lineId] = l.clientName; });
    } catch { /* sin clientes */ }
    const clients = Object.keys(clientByLine)
        .map(k => clientByLine[k])
        .filter((name, i, all) => name && all.indexOf(name) === i);

    return {
        ...mapOrder(raw),
        clients,
        notes: raw.notes || '',
        paymentTerms: raw.payment_terms_snapshot ? `${raw.payment_terms_snapshot} dias` : '',
        lines: (Array.isArray(raw.lines) ? raw.lines : []).map((l: any) => ({
            id: String(l.id),
            title: l.book_title || l.description || 'Linea sin descripcion',
            quantityOrdered: toNumber(l.quantity_ordered),
            quantityReceived: toNumber(l.quantity_received),
            unitPrice: toNumber(l.unit_price),
            lineTotal: toNumber(l.line_total),
            clientName: clientByLine[String(l.id)] || ''
        })),
        history: (Array.isArray(raw.status_history) ? raw.status_history : []).map((h: any) => ({
            status: (STATUSES.indexOf(h.status) === -1 ? 'draft' : h.status) as PurchaseOrderStatus,
            changedByName: h.changed_by_name || '',
            changedAt: new Date(h.changed_at || Date.now()),
            note: h.note || ''
        }))
    };
};

